/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, lazy, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { Button } from '@/components/ui/button';
import type { GuestOrderMeta, GuestReturnLabel, SomOrderModel } from '@/lib/order/guest-lookup.server';
import { TrackOrderSummaryView } from './order-summary-view';
import { ReturnLabelPanel } from './return-label-panel';
import { getCancelableItems, getReturnableItems } from './helpers';

// Lazy-loaded: the mutation dialogs are hidden overlays on initial render (see
// docs/README-PERFORMANCE.md#lazy-loading-for-overlays-modals-drawers-dialogs). Each subtree is
// only mounted after its trigger is first clicked, via the `*Loaded` guards below.
const TrackOrderCancelDialog = lazy(() => import('./cancel-dialog'));
const TrackOrderReturnDialog = lazy(() => import('./return-dialog'));

export interface TrackOrderResultProps {
    order: SomOrderModel;
    /** The order token from the lookup; held in React state only and passed to mutation dialogs. */
    orderToken: string;
    /** Cancel/return reason lists folded into the lookup response. */
    meta: GuestOrderMeta;
}

/**
 * Client wrapper around the read-only {@link TrackOrderSummaryView} that adds the guest
 * self-service actions (cancel + return). It owns the *live* order model in React state: after a
 * successful mutation the cartridge returns the re-fetched summary, which is swapped in here so
 * the view re-renders updated availability without re-running identity (no email/ZIP round trip)
 * and without ever persisting the token.
 *
 * A return that produces a physical RMA label surfaces the DHL label inline (base64 PDF). It is
 * held in state and rendered as a persistent {@link ReturnLabelPanel} that survives the dialog
 * closing — there is no session to read it back from later (unlike SFRA's ReturnSuccess page).
 *
 * The mutation dialogs are lazy-loaded overlays, mounted only after their trigger is first clicked.
 */
export function TrackOrderResult({ order: initialOrder, orderToken, meta }: TrackOrderResultProps): ReactElement {
    const { t } = useTranslation('trackOrder');
    const [order, setOrder] = useState<SomOrderModel>(initialOrder);
    const [returnLabel, setReturnLabel] = useState<GuestReturnLabel | null>(null);

    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelLoaded, setCancelLoaded] = useState(false);
    const cancelButtonRef = useRef<HTMLButtonElement | null>(null);

    const [returnOpen, setReturnOpen] = useState(false);
    const [returnLoaded, setReturnLoaded] = useState(false);
    const returnButtonRef = useRef<HTMLButtonElement | null>(null);

    const actionsBarRef = useRef<HTMLDivElement | null>(null);

    // Guest self-service cancel/return are individually feature-flagged (opt-out: enabled unless
    // the flag is explicitly `false`). A disabled capability hides its button entirely so the lazy
    // dialog is never mounted; the matching guest-cancel/guest-return endpoint enforces the same
    // gate server-side. Baggallini ships cancel disabled (see config.server.ts).
    const config = useConfig();
    const cancelFeatureEnabled = config.features.guestOrderManagement?.cancel !== false;
    const returnFeatureEnabled = config.features.guestOrderManagement?.return !== false;

    const canCancel = cancelFeatureEnabled && getCancelableItems(order).length > 0;
    // `meta.returnAvailability` is SFRA's `returnAvailabilityByRules` (the `returns_expiration_days`
    // window) evaluated cartridge-side. Gating here keeps a shopper from filling in a return the
    // cartridge would refuse; the cartridge enforces the same rule on preview and submit.
    const canReturn = returnFeatureEnabled && meta.returnAvailability.available && getReturnableItems(order).length > 0;

    // The action bar container is always rendered (with a `tabIndex={-1}` handle) so it stays a
    // valid fallback focus target even after a full cancel/return removes a button — otherwise
    // Radix would drop focus to <body> on dialog close.
    const actions = (
        <div className="space-y-4">
            <div
                ref={actionsBarRef}
                tabIndex={-1}
                className="flex flex-wrap gap-2 outline-none"
                data-slot="track-order-actions">
                {canCancel && (
                    <Button
                        ref={cancelButtonRef}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => {
                            setCancelLoaded(true);
                            setCancelOpen(true);
                        }}>
                        {t('actions.cancelItems')}
                    </Button>
                )}
                {canReturn && (
                    <Button
                        ref={returnButtonRef}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => {
                            setReturnLoaded(true);
                            setReturnOpen(true);
                        }}>
                        {t('actions.returnItems')}
                    </Button>
                )}
            </div>
            {/* SFRA shows `rmaItemUnavailableMessage` when the window has passed but suppresses it
                when returns are switched off wholesale — `showUnavailableMsg` carries that
                distinction through from the cartridge. Without this the button simply vanishes
                and the shopper has no idea why. */}
            {meta.returnAvailability.showUnavailableMsg && (
                <p className="text-sm text-muted-foreground">{t('actions.returnWindowExpired')}</p>
            )}
            {returnLabel && <ReturnLabelPanel label={returnLabel} />}
        </div>
    );

    return (
        <>
            <TrackOrderSummaryView order={order} actions={actions} />
            {cancelLoaded && (
                <Suspense fallback={null}>
                    <TrackOrderCancelDialog
                        order={order}
                        orderToken={orderToken}
                        cancelReasons={meta.cancelReasons}
                        open={cancelOpen}
                        onOpenChange={setCancelOpen}
                        onCancelled={(refreshed) => {
                            // Best-effort refresh: the cartridge re-fetches the summary token-only
                            // after the mutation. On a refresh miss (`null`) the cancel still
                            // succeeded — keep the current view rather than clearing it.
                            if (refreshed) setOrder(refreshed);
                        }}
                        triggerRef={cancelButtonRef}
                        fallbackFocusRef={actionsBarRef}
                    />
                </Suspense>
            )}
            {returnLoaded && (
                <Suspense fallback={null}>
                    <TrackOrderReturnDialog
                        order={order}
                        orderToken={orderToken}
                        returnReasons={meta.returnReasons}
                        otherReasonValue={meta.otherReasonValue}
                        open={returnOpen}
                        onOpenChange={setReturnOpen}
                        onReturned={(refreshed, label) => {
                            // Same best-effort refresh as cancel; also surface the DHL label (when a
                            // physical RMA return produced one) as a persistent download panel.
                            if (refreshed) setOrder(refreshed);
                            setReturnLabel(label);
                        }}
                        triggerRef={returnButtonRef}
                        fallbackFocusRef={actionsBarRef}
                    />
                </Suspense>
            )}
        </>
    );
}

export default TrackOrderResult;
